import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PageHeader, ExportBar, VerifiedTag } from "@/components/EntityPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";

function VendorForm({ initial, onSubmit }) {
  const [f, setF] = useState(initial || {});
  const [users, setUsers] = useState([]);
  const [copyWA, setCopyWA] = useState(!!initial && initial.mobile && initial.mobile === initial.whatsapp);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  useEffect(() => {
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role === "vendor"))).catch(() => {});
  }, []);
  function onMobile(e) {
    const v = e.target.value;
    setF((s) => ({ ...s, mobile: v, whatsapp: copyWA ? v : s.whatsapp }));
  }
  function toggleCopy(v) {
    setCopyWA(!!v); if (v) setF((s) => ({ ...s, whatsapp: s.mobile || s.whatsapp }));
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Vendor name</Label>
          <Input required value={f.name || ""} onChange={set("name")} data-testid="vendor-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Mobile</Label>
          <Input value={f.mobile || ""} onChange={onMobile} data-testid="vendor-mobile" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">WhatsApp</Label>
          <Input value={f.whatsapp || ""} onChange={set("whatsapp")} disabled={copyWA} data-testid="vendor-whatsapp" className={`bg-[hsl(224_40%_12%)] mt-1 ${copyWA ? "opacity-70" : ""}`}/>
          <label className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
            <input type="checkbox" checked={copyWA} onChange={(e) => toggleCopy(e.target.checked)} data-testid="vendor-wa-same" className="accent-amber-500 h-3.5 w-3.5"/>
            Same as mobile — auto-copy
          </label>
        </div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Aadhaar</Label>
          <Input value={f.aadhaar || ""} onChange={set("aadhaar")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="flex items-end">
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!f.aadhaar_verified} onChange={(e) => setF({ ...f, aadhaar_verified: e.target.checked })} className="accent-amber-500"/> Aadhaar verified</label>
        </div>
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Address</Label>
          <Input value={f.address || ""} onChange={set("address")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase text-amber-300/80">Link to login account (role: vendor)</Label>
          <select value={f.linked_user_id || ""} onChange={(e) => setF({ ...f, linked_user_id: e.target.value || null })}
            data-testid="vendor-linked-user"
            className="w-full mt-1 h-9 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
            <option value="">— not linked —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">Pick the login account created in Users &amp; Roles. That user will see this vendor's ledger.</div>
        </div>
      </div>
      <div className="flex justify-end"><Button type="submit" data-testid="vendor-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{initial ? "Update" : "Create"}</Button></div>
    </form>
  );
}

export default function Vendors() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() { const { data } = await api.get("/vendors"); setRows(data); }
  useEffect(() => { load(); }, []);
  async function save(f) {
    try {
      if (editing) await api.put(`/vendors/${editing.id}`, f); else await api.post("/vendors", f);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/vendors/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Vendors" subtitle="Partner vendors and their verification status."
        right={<>
          <ExportBar rows={filtered.map((v) => ({ name: v.name, mobile: v.mobile, whatsapp: v.whatsapp, aadhaar: v.aadhaar, address: v.address }))} filename="vendors" whatsappText={`Vendors: ${filtered.length}`}/>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-vendors-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New vendor</Button></DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "New"} vendor</DialogTitle></DialogHeader>
              <VendorForm initial={editing} onSubmit={save}/>
            </DialogContent>
          </Dialog>
        </>}
      />
      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter vendors…" data-testid="filter-input" className="bg-transparent border-0 focus-visible:ring-0 h-8"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
            {["Vendor","Aadhaar","Mobile","WhatsApp","Address","Actions"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No records</td></tr> :
              filtered.map((v) => (
                <tr key={v.id} className="border-t border-border">
                  <td className="p-3">{v.name}</td>
                  <td className="p-3"><div className="flex flex-col gap-1"><span className="font-mono text-xs">{v.aadhaar || "—"}</span><VerifiedTag ok={v.aadhaar_verified}/></div></td>
                  <td className="p-3 font-mono text-xs">{v.mobile || "—"}</td>
                  <td className="p-3 font-mono text-xs">{v.whatsapp || "—"}</td>
                  <td className="p-3 text-xs">{v.address || "—"}</td>
                  <td className="p-3"><div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-vendor-${v.id}`} className="h-8 w-8" onClick={() => { setEditing(v); setOpen(true); }}><Pencil className="w-3.5 h-3.5"/></Button>
                    <Button variant="ghost" size="icon" data-testid={`del-vendor-${v.id}`} className="h-8 w-8 hover:text-red-400" onClick={() => del(v.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
